(ns hai.ui.components.dropdown-menu
  "Reusable dropdown menu component with support for nested menus, icons, and separators"
  (:require
   [hai.ui.components :as c]
   [lambdaisland.ornament :as o]))

(declare render-menu-item)

(o/defstyled menu-item-icon :span
  :inline-flex :items-center :justify-center
  :transition-transform :duration-200 :ease-in-out
  ;; Inherit the menu item's text colour so the icon tracks it automatically:
  ;; gray-12 normally, and the accent contrast colour (white) when the parent
  ;; item is highlighted on hover/keyboard — instead of staying dark on dark.
  [:svg {:fill "currentColor"}]
  ["&[data-highlighted]" {:transform "rotate(5deg)"}]
  ([icon]
   [:<>
    [icon]]))

(o/defstyled menu-item-content :div
  :flex :items-center :gap-2
  ([icon label]
   [:<>
    (when icon [menu-item-icon icon])
    label]))

(defn render-submenu
  "Renders a submenu using DropdownMenuSub components"
  [{:keys [label icon items disabled?]} & {:keys [container]}]
  [c/DropdownMenuSub #_(when container {:container container})
   [c/DropdownMenuSubTrigger
    {:disabled disabled?}
    [menu-item-content icon label]]
   (into [c/DropdownMenuSubContent (when container {:container container})]
         (map render-menu-item)
         items)])

(defn render-menu-item
  "Renders a single menu item, which can be a regular item, separator, group label, or submenu"
  [{:keys [type label icon on-select items disabled?] :as item}
   & {:keys [container]}]
  (cond
    ;; Separator
    (= type :separator)
    [c/DropdownMenuSeparator {:key (gensym "sep-")}]

    ;; Group label
    (= type :group-label)
    [c/DropdownMenuLabel label]

    ;; Submenu (has nested items)
    (seq items)
    [render-submenu item (when container {:container container})]

    ;; Regular menu item
    :else
    [c/DropdownMenuItem
     {:onSelect (when on-select
                  (fn [e]
                    (on-select e)))
      :disabled disabled?}
     [menu-item-content icon label]]))

(o/defstyled dropdown-menu-root :div
  :inline-block
  ([{:keys [container trigger items open? on-open-change on-close-auto-focus]}]
   [c/DropdownMenuRoot
    (cond-> {:modal false} ; Prevent body scroll lock to avoid page width shift
      (some? open?) (assoc :open open?)
      on-open-change (assoc :onOpenChange on-open-change))
    [c/DropdownMenuTrigger
     {:asChild true}
     trigger]
    (into [c/DropdownMenuContent (cond-> {}
                                   container (assoc :container container)
                                   on-close-auto-focus (assoc :onCloseAutoFocus on-close-auto-focus))]
          (map #(render-menu-item % (when container {:container container})))
          items)]))

(defn dropdown-menu
  "Main dropdown menu component.

   Props:
   - :container - HTMLElement used to populate portal
   - :trigger - The element that triggers the dropdown (e.g., a button)
   - :items - Vector of menu items with the following structure:
     - :label - Text to display
     - :icon - Optional icon component
     - :on-select - Function to call when selected
     - :disabled? - Whether the item is disabled
     - :items - Nested items for submenus
     - :type - Special types:
       - :separator - Renders a visual separator
       - :group-label - Renders a non-interactive label for grouping items
   - :open? - Controlled open state (optional)
   - :on-open-change - Callback for open state changes (optional)

   Example:
   [dropdown-menu
    {:trigger [c/Button \"Menu\"]
     :items [{:type :group-label
              :label \"File Operations\"}
             {:label \"Edit\"
              :icon icons/edit
              :on-select #(println \"Edit\")}
             {:type :separator}
             {:label \"More options\"
              :items [{:label \"Option 1\"
                       :on-select #(println \"Option 1\")}
                      {:label \"Option 2\"
                       :on-select #(println \"Option 2\")}]}]}]"
  [props]
  [dropdown-menu-root props])
